import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { UploadSimple, CheckCircle, Warning, FileCsv } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import GeocodeResolverPanel from "../components/crm/GeocodeResolverPanel";

const STATUS_STYLE = {
  Ready: "bg-green-50 text-green-700 border-green-200",
  Duplicate: "bg-yellow-50 text-yellow-800 border-yellow-200",
  Incomplete: "bg-amber-50 text-amber-800 border-amber-200",
  Error: "bg-red-50 text-red-700 border-red-200",
};

export default function ImportCSV() {
  const [file, setFile] = useState(null);
  const [defaultSource, setDefaultSource] = useState("Manual");
  const [leadSources, setLeadSources] = useState(["Manual"]);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary, default_lead_source }
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/meta/enums").then(({ data }) => {
      setLeadSources(data.lead_sources || ["Manual"]);
    }).catch(() => {});
  }, []);

  const resetAll = () => {
    setPreview(null);
    setSelected(new Set());
    setResult(null);
  };

  const runPreview = async (f, source) => {
    setPreviewing(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("default_lead_source", source);
      const { data } = await api.post("/leads/csv/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Default selection: every Ready row
      const initSel = new Set(
        data.rows.filter((r) => r.status === "Ready").map((r) => r.row_index)
      );
      setSelected(initSel);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    resetAll();
    runPreview(f, defaultSource);
  };

  // Re-run preview when default source changes (only if a file is loaded)
  useEffect(() => {
    if (file) runPreview(file, defaultSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSource]);

  const toggleRow = (idx, status) => {
    if (status !== "Ready") return; // disabled
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAllImportable = () => {
    if (!preview) return;
    setSelected(
      new Set(preview.rows.filter((r) => r.status === "Ready").map((r) => r.row_index))
    );
  };

  const deselectAll = () => setSelected(new Set());

  const importSelected = async () => {
    if (!file || selected.size === 0) {
      toast.error("Select at least one row to import");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("default_lead_source", defaultSource);
      form.append(
        "selected_indexes",
        Array.from(selected).sort((a, b) => a - b).join(",")
      );
      const { data } = await api.post("/leads/csv/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.summary.created} lead${data.summary.created === 1 ? "" : "s"}`);
      // After successful import, re-preview so duplicates of just-imported rows are reflected
      if (data.summary.created > 0) {
        await runPreview(file, defaultSource);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/csv/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gardaj_leads_template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const readyCount = preview?.summary?.ready ?? 0;
  const totalCount = preview?.summary?.total ?? 0;
  const selectedCount = selected.size;
  const allReadySelected = useMemo(
    () => preview && readyCount > 0 && selectedCount === readyCount,
    [preview, readyCount, selectedCount]
  );

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Import</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Import leads from CSV</h1>
        <p className="text-sm text-gray-500 mt-1">
          Preview classifies each row as Ready, Duplicate, Incomplete, or Error. Only Ready rows can be imported.
          Duplicates are matched by VAT, email, phone, or company+country (against the CRM and inside the uploaded file).
          Required: company_name, contact_name, country, city, plus email or phone.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1A3A21] text-white hover:bg-[#142C19] cursor-pointer"
            data-testid="csv-file-label">
            <UploadSimple size={16} /> Select CSV
            <input type="file" accept=".csv" className="hidden"
              data-testid="csv-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          <button onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 text-sm text-[#1A3A21] font-semibold hover:underline"
            data-testid="download-template">
            <FileCsv size={16} weight="duotone" /> Download template
          </button>
          {file && (
            <div className="text-sm text-gray-600" data-testid="csv-file-name">
              {file.name} {totalCount > 0 && <span>· {totalCount} row{totalCount === 1 ? "" : "s"} parsed</span>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs font-medium uppercase tracking-wider text-gray-500">
            Default Lead Source (when missing in file)
          </label>
          <div className="w-56">
            <Select value={defaultSource} onValueChange={setDefaultSource}>
              <SelectTrigger data-testid="default-lead-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {leadSources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {previewing && (
          <div className="text-sm text-gray-500" data-testid="preview-loading">Analyzing rows…</div>
        )}

        {preview && !previewing && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="preview-summary">
              <SummaryTile label="Total rows" value={preview.summary.total} testid="summary-total" />
              <SummaryTile label="Ready" value={preview.summary.ready} tone="green" testid="summary-ready" />
              <SummaryTile label="Duplicates" value={preview.summary.duplicate} tone="yellow" testid="summary-duplicate" />
              <SummaryTile label="Incomplete + Errors"
                value={preview.summary.incomplete + preview.summary.error}
                tone="red" testid="summary-incomplete-error" />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllImportable}
                  disabled={readyCount === 0 || allReadySelected}
                  className="text-xs px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                  data-testid="select-all-importable">
                  Select all importable ({readyCount})
                </button>
                <button
                  type="button"
                  onClick={deselectAll}
                  disabled={selectedCount === 0}
                  className="text-xs px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                  data-testid="deselect-all">
                  Deselect all
                </button>
              </div>
              <div className="text-xs text-gray-600" data-testid="selected-counter">
                <span className="font-semibold text-[#1A3A21]">{selectedCount}</span> selected
              </div>
            </div>

            <div className="border border-gray-200 rounded-md overflow-auto max-h-[28rem]">
              <table className="text-xs w-full" data-testid="csv-preview-table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold w-10"></th>
                    <th className="text-left py-2 px-3 font-semibold w-12">#</th>
                    <th className="text-left py-2 px-3 font-semibold w-24">Status</th>
                    <th className="text-left py-2 px-3 font-semibold">Reason</th>
                    <th className="text-left py-2 px-3 font-semibold">Company</th>
                    <th className="text-left py-2 px-3 font-semibold">Contact</th>
                    <th className="text-left py-2 px-3 font-semibold">Country</th>
                    <th className="text-left py-2 px-3 font-semibold">City</th>
                    <th className="text-left py-2 px-3 font-semibold">Email</th>
                    <th className="text-left py-2 px-3 font-semibold">Phone</th>
                    <th className="text-left py-2 px-3 font-semibold">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const importable = r.status === "Ready";
                    const checked = selected.has(r.row_index);
                    const reason = (r.reasons || []).join("; ");
                    return (
                      <tr key={r.row_index}
                        data-testid={`preview-row-${r.row_index}`}
                        className={`border-t border-gray-100 ${importable ? "" : "bg-gray-50/60"}`}>
                        <td className="py-1.5 px-3 align-top">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!importable}
                            onChange={() => toggleRow(r.row_index, r.status)}
                            data-testid={`row-checkbox-${r.row_index}`}
                            aria-label={`Select row ${r.row_index}`}
                          />
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-500">{r.row_index}</td>
                        <td className="py-1.5 px-3 align-top">
                          <span
                            data-testid={`row-status-${r.row_index}`}
                            className={`inline-block text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${STATUS_STYLE[r.status] || ""}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-600 max-w-[240px]"
                          data-testid={`row-reason-${r.row_index}`}
                          title={reason}>
                          {reason || (importable ? "—" : "")}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-800 truncate max-w-[180px]">
                          {r.preview.company_name || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700 truncate max-w-[140px]">
                          {r.preview.contact_name || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700">
                          {r.preview.country || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700">
                          {r.preview.city || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700 truncate max-w-[180px]">
                          {r.preview.email || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700">
                          {r.preview.phone || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 px-3 align-top text-gray-700">
                          {r.preview.vat_number || <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <button
                onClick={importSelected}
                disabled={importing || selectedCount === 0}
                data-testid="import-selected-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E1A104] text-[#1A3A21] font-semibold rounded-md hover:bg-[#C98F03] disabled:opacity-50">
                {importing ? "Importing…" : `Import ${selectedCount} selected`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="import-result">
            <ResultTile label="Created" value={result.summary.created} tone="green" Icon={CheckCircle} />
            <ResultTile label="Skipped duplicates" value={result.summary.skipped} tone="yellow" Icon={Warning} />
            <ResultTile label="Errors / incomplete" value={result.summary.errors} tone="red" Icon={Warning} />
            <ResultTile label="Skipped deselected" value={result.summary.skipped_deselected || 0} tone="gray" Icon={Warning} />
            {(result.errors?.length > 0 || result.skipped?.length > 0) && (
              <div className="md:col-span-4 text-xs text-gray-700 max-h-40 overflow-y-auto bg-gray-50 border border-gray-200 rounded-md p-3"
                data-testid="import-result-details">
                {result.errors?.map((e, i) => <div key={`e${i}`}>Row {e.row}: {e.error}</div>)}
                {result.skipped?.map((s, i) => <div key={`s${i}`}>Row {s.row} skipped — {s.reason}{s.match ? ` (${s.match})` : ""}</div>)}
              </div>
            )}
          </div>
        )}

        {/* GEO-2: Admin-only location-resolution panel shown after a successful import. */}
        {result && result.summary?.created > 0 && (
          <GeocodeResolverPanel
            contextLabel="Some imported leads may have city/country data but no coordinates yet. Preview unmapped pairs, then geocode up to 50 valid locations using the backend cache. Import is already saved — geocoding is optional."
          />
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone = "gray", testid }) {
  const toneCls = {
    green: "border-green-200 bg-green-50",
    yellow: "border-yellow-200 bg-yellow-50",
    red: "border-red-200 bg-red-50",
    gray: "border-gray-200 bg-white",
  }[tone];
  return (
    <div className={`border ${toneCls} rounded-md p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function ResultTile({ label, value, tone, Icon }) {
  const toneCls = {
    green: "border-green-200 bg-green-50 text-green-700",
    yellow: "border-yellow-200 bg-yellow-50 text-yellow-700",
    red: "border-red-200 bg-red-50 text-red-700",
    gray: "border-gray-200 bg-gray-50 text-gray-700",
  }[tone];
  return (
    <div className={`border rounded-md p-4 ${toneCls}`}>
      <div className="flex items-center gap-2"><Icon weight="bold" /> {label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
